/**
 * B1-cfg 法大大电子签配置（ControlPanel ✍️ 电子签 tab）
 *
 * 仿 SmsConfig 结构：状态卡 + 启停开关 + 测连接 + 编辑弹窗。
 * 后端：/esign-providers（GET 列表 mask） · /esign-providers/:id（PUT 保存） · /esign-providers/:id/test（POST 测连接）
 * 拍板 A：专表 esign_providers；B：token 走 Redis。AppSecret 前端永不显示明文，只 mask。
 */
import { useState } from 'react';
import { Input, message, Modal, Switch, Tag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Btn, Pill } from '../../components/ui';
import { fmtMonthDayTime } from '../../lib/format';

type Provider = {
  providerId: string;
  displayName: string;
  enabled: boolean;
  configured: boolean;
  appId: string;
  appSecretMasked: string;
  baseUrl: string;
  apiSubVersion: string;
  sealId: string;
  signTemplateId: string;
  lastTestedAt: string | null;
  lastTestResult: { ok: boolean; latency_ms?: number; error?: string; token_preview?: string } | null;
};

const PROVIDER_ID = 'fadada';

export function FadadaConfig() {
  const qc = useQueryClient();
  const { data: providers, isLoading } = useQuery<Provider[]>({
    queryKey: ['esign-providers'],
    queryFn: () => $.get('/esign-providers'),
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    app_id: '', app_secret: '', base_url: '', api_sub_version: '', seal_id: '', sign_template_id: '',
  });

  const p = (providers ?? []).find((x) => x.providerId === PROVIDER_ID);

  const openEdit = () => {
    // app_secret 始终留空（=不修改）；其余预填当前值便于微调
    setForm({
      app_id: p?.appId ?? '',
      app_secret: '',
      base_url: p?.baseUrl ?? '',
      api_sub_version: p?.apiSubVersion ?? '5.1',
      seal_id: p?.sealId ?? '',
      sign_template_id: p?.signTemplateId ?? '',
    });
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () => {
      // app_secret 留空则不下发该字段，避免清空已存凭证
      const payload: any = { ...form };
      if (!payload.app_secret) delete payload.app_secret;
      return $.put(`/esign-providers/${PROVIDER_ID}`, payload);
    },
    onSuccess: () => {
      message.success('法大大配置已保存');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['esign-providers'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => $.put(`/esign-providers/${PROVIDER_ID}`, { enabled }),
    onSuccess: () => {
      message.success('已更新启用状态');
      qc.invalidateQueries({ queryKey: ['esign-providers'] });
    },
    onError: (e: any) => message.error(e?.message ?? '更新失败'),
  });

  const test = useMutation({
    mutationFn: () => $.post(`/esign-providers/${PROVIDER_ID}/test`, {}),
    onSuccess: (r: any) => {
      if (r?.ok) message.success(`连接成功 · ${r.latency_ms}ms`);
      else message.error(`连接失败：${r?.error ?? '未知错误'}`);
      qc.invalidateQueries({ queryKey: ['esign-providers'] });
    },
    onError: (e: any) => message.error(e?.message ?? '测试失败'),
  });

  if (isLoading || !p) return <div style={{ padding: 40, textAlign: 'center', color: '#8F959E' }}>加载中…</div>;

  const lr = p.lastTestResult;

  return (
    <div>
      {/* 状态汇总 */}
      <div style={{
        padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            法大大电子签凭证
            {p.configured
              ? <Pill tone="success">已配置</Pill>
              : <Pill tone="danger">未配置</Pill>}
            {p.enabled
              ? <Pill tone="success">已启用</Pill>
              : <Pill tone="warning">未启用</Pill>}
          </div>
          <div style={{ fontSize: 12, color: '#646A73', marginTop: 4 }}>
            AppSecret AES-256 加密存专表 esign_providers，前端永不显示明文。启用后合同审批通过将自动发起签署（B1-flow）
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#646A73' }}>启用</span>
          <Switch
            checked={p.enabled}
            disabled={!p.configured || toggle.isPending}
            onChange={(v) => toggle.mutate(v)}
          />
          <Btn kind="primary" onClick={openEdit}>编辑配置</Btn>
        </div>
      </div>

      {/* 配置项展示 */}
      <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
        <Row label="AppId" value={p.appId || '（未配置）'} mono />
        <Row label="AppSecret" value={p.appSecretMasked || '（未配置）'} mono />
        <Row label="域名 (base_url)" value={p.baseUrl || '（未配置）'} mono />
        <Row label="API 子版本" value={p.apiSubVersion} mono />
        <Row label="印章 id (seal_id)" value={p.sealId || '（未填，B1-sign 用）'} mono />
        <Row label="签署模板 id (sign_template_id)" value={p.signTemplateId || '（未填，B1-sign 用）'} mono />
      </div>

      {/* 测连接 */}
      <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>⚡ 测试连接</div>
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          用当前凭证向法大大换一次 accessToken（不写缓存），验证 AppId / AppSecret / 域名是否正确。
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn kind="primary" onClick={() => test.mutate()} disabled={!p.configured || test.isPending}>
            {test.isPending ? '测试中…' : '测试连接'}
          </Btn>
          {lr && (
            <span style={{ fontSize: 12, color: lr.ok ? '#0FA968' : '#F5483B' }}>
              {lr.ok ? <Tag color="green">OK</Tag> : <Tag color="red">FAIL</Tag>}
              {lr.ok
                ? `换取成功 · ${lr.latency_ms}ms · token ${lr.token_preview ?? ''}`
                : `失败：${lr.error}`}
              {p.lastTestedAt && <span style={{ marginLeft: 8, color: '#8F959E' }}>· {fmtMonthDayTime(p.lastTestedAt)}</span>}
            </span>
          )}
        </div>
      </div>

      {/* 编辑 Modal */}
      <Modal open={editing} title="编辑法大大配置"
             onCancel={() => setEditing(false)}
             onOk={() => save.mutate()} okText="保存"
             cancelText="取消" confirmLoading={save.isPending} width={600}>
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          AppSecret 留空 = 不修改；其余字段直接覆盖。域名以法大大开放平台控制台为准。
        </div>
        <FormRow label="AppId（X-FASC-App-Id）" value={form.app_id} onChange={(v) => setForm({ ...form, app_id: v })} placeholder="法大大控制台 应用 AppId" />
        <FormRow label="AppSecret" value={form.app_secret} onChange={(v) => setForm({ ...form, app_secret: v })}
                 placeholder={p.configured ? '留空 = 不修改；输入新值覆盖' : '输入 AppSecret'} password />
        <FormRow label="域名 (base_url)" value={form.base_url} onChange={(v) => setForm({ ...form, base_url: v })} placeholder="如 https://api-sandbox.fadada.com（沙箱，以控制台为准）" />
        <FormRow label="API 子版本" value={form.api_sub_version} onChange={(v) => setForm({ ...form, api_sub_version: v })} placeholder="5.1" />
        <FormRow label="印章 id (seal_id，可选)" value={form.seal_id} onChange={(v) => setForm({ ...form, seal_id: v })} placeholder="企业自动盖章印章 id（B1-sign 用）" />
        <FormRow label="签署模板 id (sign_template_id，可选)" value={form.sign_template_id} onChange={(v) => setForm({ ...form, sign_template_id: v })} placeholder="合同签署模板 id（B1-sign 用）" />
      </Modal>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0F2F5' }}>
      <div style={{ width: 240, fontSize: 13, color: '#646A73' }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, fontFamily: mono ? 'monospace' : undefined }}>{value}</div>
    </div>
  );
}

function FormRow({ label, value, onChange, placeholder, password }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; password?: boolean;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>{label}</div>
      {password ? (
        <Input.Password value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
